import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ShareBar from '@/pages/detail/components/ShareBar';
import BackToTop from '@/pages/detail/components/BackToTop';
import { usePageMeta } from '@/hooks/usePageMeta';
import { formatNumber, getArticleById, getRelatedArticles } from '@/utils/news';

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);

  usePageMeta(
    article ? `${article.title} - 热搜榜资讯详情` : '资讯详情 - 热搜榜',
    article
      ? `${article.summary} 关键词：${article.tags.join('、')}。热搜榜为你带来${article.categoryName}领域的热点追踪。`
      : '热搜榜资讯详情页。',
  );

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center text-5xl text-foreground-300">
            <i className="ri-emotion-sad-line"></i>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">你查看的资讯可能已被移除或链接有误。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const related = getRelatedArticles(article, 5);
  const readMinutes = Math.max(1, Math.round(article.content.join('').length / 300));

  return (
    <Layout>
      <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: `${article.categoryName}热点`, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${article.category}`}
                  className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-newspaper-line"></i>
                  {article.source}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.publishTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line text-accent-500"></i>
                  {formatNumber(article.hot)} 热度
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatNumber(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-book-open-line"></i>
                  约 {readMinutes} 分钟阅读
                </span>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 热搜榜`}
                  className="h-56 w-full object-cover object-top md:h-96"
                />
              </div>

              <p className="mt-6 border-l-4 border-primary-300 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {article.summary}
              </p>

              <div className="mt-6 flex flex-col gap-5">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-base leading-8 text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-400">关键词</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="text-xs text-primary-600 transition-colors hover:text-primary-700"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/category/${article.category}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回{article.categoryName}
                  </Link>
                  <ShareBar title={article.title} />
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900 md:text-xl">
                <i className="ri-links-line text-primary-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="grid" />
                ))}
              </div>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                  <i className="ri-fire-fill text-accent-500"></i>
                  相关热门
                </h2>
                <div className="flex flex-col divide-y divide-background-200">
                  {related.slice(0, 5).map((item, index) => (
                    <ArticleCard key={item.id} article={item} variant="compact" rank={index + 1} />
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-primary-50 p-4">
                <h2 className="font-heading text-base font-bold text-foreground-900">浏览提示</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本站资讯均为前端演示样例。你可以在分类页按时间段、热点标签与排序方式筛选更多内容，或使用顶部搜索框按关键词检索。
                </p>
                <Link
                  to="/"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  回到首页查看更多热点
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BackToTop />
    </Layout>
  );
}